/**
 * Shivurt's appearance switch — the visitor's own light/dark choice, on top of
 * the site's designed default.
 *
 * The shell owns ONE theme signal: a `light`/`dark` class on the document root,
 * applied once from `SITE_CONFIG.appearance`. So this module works with that
 * signal rather than around it:
 *
 * - `resolveInitialAppearance()` is what `site-config.ts` passes as the site's
 *   appearance, so the visitor's stored choice is already correct at first
 *   paint — no flash of the wrong ground, and nothing to override afterwards.
 *   It always resolves to a concrete `light`/`dark` (never `auto`) so the shell
 *   attaches no OS listener that could later fight an explicit choice.
 * - `applyAppearance()` moves the same class afterwards, and repeats the two
 *   side effects the shell performs with it: the `theme-color` meta (Safari
 *   tints its own chrome from it) and the iOS 26 toolbar-tint resample.
 *
 * The choice is per session (`sessionStorage`), which is the honest scope for a
 * visitor preference on an anonymous trade desk: it survives a reload of the
 * same tab and leaves nothing behind. With no stored choice we follow the
 * visitor's OS via `prefers-color-scheme`.
 */
import { useCallback, useEffect, useState } from 'react';

export type Appearance = 'light' | 'dark';

const STORAGE_KEY = 'shivurt.appearance';

/** sessionStorage throws in some embedded/blocked contexts — never fatal here. */
function readStored(): Appearance | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function writeStored(mode: Appearance): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A blocked store costs persistence, not the toggle.
  }
}

function systemAppearance(): Appearance {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/** The appearance the site should open in: stored choice, else the visitor's OS. */
export function resolveInitialAppearance(): Appearance {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return readStored() ?? systemAppearance();
}

export function applyAppearance(mode: Appearance): void {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.classList.toggle('light', mode === 'light');

  const canvasColor = getComputedStyle(root).backgroundColor;
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (canvasColor && themeColorMeta) {
    themeColorMeta.setAttribute('content', canvasColor);
  }

  // iOS 26 samples toolbar tint at initial render only; a display round-trip
  // re-enters the render tree and forces a fresh sample.
  const strip = document.getElementById('edge-tint');
  if (strip) {
    strip.style.display = 'none';
    strip.getBoundingClientRect();
    strip.style.display = '';
  }
}

export function useAppearance(): { mode: Appearance; toggle: () => void } {
  const [mode, setMode] = useState<Appearance>(() => resolveInitialAppearance());

  // Keeps the DOM in step with a choice made in another mounted copy of the
  // toggle (mobile sidebar + desktop bar), and re-asserts after any remount.
  useEffect(() => {
    applyAppearance(mode);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((current) => {
      const next: Appearance = current === 'dark' ? 'light' : 'dark';
      writeStored(next);
      applyAppearance(next);
      return next;
    });
  }, []);

  return { mode, toggle };
}
